import React, { useState } from "react";
import { toast } from "sonner";
import { Send, Phone, Mail, MapPin } from "lucide-react";
import api from "@/lib/api";

const EMPTY = { name: "", email: "", phone: "", subject: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);

  const onChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (
      !form.name ||
      !form.email ||
      !form.phone ||
      !form.subject ||
      !form.message
    ) {
      toast.error("Please fill in every field.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Thank you. Our team will reach out shortly.");
      setForm(EMPTY);
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.message ||
        "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      className="relative py-24 md:py-32 border-t border-white/5"
      data-testid="contact-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
          <div className="lg:col-span-5">
            <div className="text-[11px] uppercase tracking-[0.4em] text-[#D4AF37] mb-5">
              Connect With Us
            </div>
            <h2 className="font-serif text-4xl md:text-5xl text-white leading-tight mb-8">
              Let’s build
              <br />
              <span className="italic text-[#D4AF37]">without chaos.</span>
            </h2>
            <p className="text-[#A3A3A3] leading-relaxed mb-10 max-w-md">
              Share a brief. Our principals will personally review your enquiry
              and respond within one business day.
            </p>

            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <Phone className="text-[#D4AF37] mt-1" size={18} />
                <div>
                  <div className="text-xs uppercase tracking-[0.25em] text-[#737373] mb-1">
                    Phone
                  </div>
                  <div className="text-white">+91 90190 35307</div>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Mail className="text-[#D4AF37] mt-1" size={18} />
                <div>
                  <div className="text-xs uppercase tracking-[0.25em] text-[#737373] mb-1">
                    Email
                  </div>
                  <a
                    href="mailto:thepegasusinfraa@gmail.com"
                    className="text-white hover:text-[#D4AF37] break-all"
                  >
                    thepegasusinfraa@gmail.com
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <MapPin className="text-[#D4AF37] mt-1" size={18} />
                <div>
                  <div className="text-xs uppercase tracking-[0.25em] text-[#737373] mb-1">
                    Studio
                  </div>
                  <div className="text-white">
                    9/39, 21st Main, Nandhini Layout,
                    <br />
                    Bangalore
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <form
              onSubmit={submit}
              className="bg-[#141414] border border-white/10 p-8 md:p-12 space-y-6"
              data-testid="contact-form"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField
                  label="Full Name"
                  name="name"
                  value={form.name}
                  onChange={onChange}
                  testid="contact-name"
                  required
                />
                <FormField
                  label="Email"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={onChange}
                  testid="contact-email"
                  required
                />
                <FormField
                  label="Phone"
                  name="phone"
                  value={form.phone}
                  onChange={onChange}
                  testid="contact-phone"
                  required
                />
                <FormField
                  label="Subject"
                  name="subject"
                  value={form.subject}
                  onChange={onChange}
                  testid="contact-subject"
                  required
                />
              </div>
              <FormField
                label="Project Brief"
                name="message"
                value={form.message}
                onChange={onChange}
                testarea
                testid="contact-message"
                required
              />

              <button
                type="submit"
                disabled={loading}
                className="group inline-flex items-center gap-3 bg-[#D4AF37] hover:bg-[#E5C665] text-black px-8 py-4 text-sm tracking-wide transition-colors disabled:opacity-60"
                data-testid="contact-submit"
              >
                {loading ? "Sending…" : "Send Enquiry"}
                <Send
                  size={15}
                  className="transition-transform group-hover:translate-x-1"
                />
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

function FormField({
  label,
  name,
  value,
  onChange,
  type = "text",
  testarea = false,
  testid,
  required,
}) {
  const base =
    "w-full bg-[#0A0A0A] border border-[#2A2A2A] text-white px-4 py-3.5 outline-none focus:border-[#D4AF37] transition-colors placeholder:text-[#737373]";
  return (
    <label className="block">
      <span className="block text-[11px] uppercase tracking-[0.25em] text-[#D4AF37] mb-2">
        {label}
      </span>
      {testarea ? (
        <textarea
          name={name}
          value={value}
          onChange={onChange}
          required={required}
          rows={5}
          className={base}
          data-testid={testid}
        />
      ) : (
        <input
          type={type}
          name={name}
          value={value}
          onChange={onChange}
          required={required}
          className={base}
          data-testid={testid}
        />
      )}
    </label>
  );
}
